import React from 'react'
import Image from 'next/image'
import type { IconType } from 'react-icons'
import { FaGithub, FaLinkedinIn } from 'react-icons/fa'
import { FaXTwitter } from 'react-icons/fa6'
import { TbWorld } from 'react-icons/tb'

import type { AmbassadorsBlock } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

export type AmbassadorData = NonNullable<AmbassadorsBlock['ambassadors']>[number]

type SocialKey = 'github' | 'x' | 'linkedin' | 'website'

const SOCIAL_PLATFORMS: ReadonlyArray<{
  key: SocialKey
  label: string
  icon: IconType
}> = [
  { key: 'github', label: 'GitHub', icon: FaGithub },
  { key: 'x', label: 'X', icon: FaXTwitter },
  { key: 'linkedin', label: 'LinkedIn', icon: FaLinkedinIn },
  { key: 'website', label: 'Website', icon: TbWorld },
]

interface Props {
  ambassador: AmbassadorData
}

export const AmbassadorCard: React.FC<Props> = ({ ambassador }) => {
  const { avatar, name, jobTitle, bio, socials } = ambassador

  const avatarUrl =
    typeof avatar === 'object' && avatar?.url ? getMediaUrl(avatar.url, avatar.updatedAt) : null
  const avatarAlt =
    (typeof avatar === 'object' && avatar?.alt) || `Portrait of ${name}`

  const activeSocials = SOCIAL_PLATFORMS.filter(
    (platform) => socials && typeof socials[platform.key] === 'string' && socials[platform.key],
  )

  const transitionBase = 'transition-colors duration-300 ease-in-out'
  const bgOnHover = `group-hover:bg-black group-focus-within:bg-black ${transitionBase}`
  const textOnHover = `group-hover:text-white group-focus-within:text-white ${transitionBase}`
  const accentOnHover = `group-hover:bg-brand-dark group-focus-within:bg-brand-dark ${transitionBase}`
  const accentBorderOnHover = `group-hover:border-brand-dark group-focus-within:border-brand-dark ${transitionBase}`

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[20px] border-2 border-black bg-white">
      <div className={cn('flex flex-1 flex-col gap-5 p-4 md:p-7 bg-white', bgOnHover)}>
        <div
          className={cn(
            'flex items-center justify-center size-20 shrink-0 overflow-hidden rounded-full border-2 border-black bg-white',
            accentBorderOnHover,
          )}
        >
          {avatarUrl && (
            <div
              className={cn(
                'relative size-20 mix-blend-luminosity transition-transform duration-300 ease-in-out',
                'motion-safe:group-hover:scale-105 motion-safe:group-focus-within:scale-105',
              )}
            >
              <Image
                src={avatarUrl}
                alt={avatarAlt}
                fill
                sizes="80px"
                className="object-cover"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5 w-full">
          <h3
            className={cn(
              'font-display font-light text-[30px] leading-9 tracking-[-0.3px] text-black',
              textOnHover,
            )}
          >
            {name}
          </h3>
          <div
            className={cn(
              'h-1 w-8 bg-black transition-all duration-300 ease-in-out',
              'group-hover:w-20 group-hover:bg-brand-dark',
              'group-focus-within:w-20 group-focus-within:bg-brand-dark',
            )}
            aria-hidden="true"
          />
          <p
            className={cn(
              'font-sans font-medium text-sm leading-5 tracking-[-0.098px] text-black',
              textOnHover,
            )}
          >
            {jobTitle}
          </p>
        </div>

        <p
          className={cn(
            'font-sans font-normal text-base leading-6 tracking-[-0.096px] text-black/70',
            textOnHover,
          )}
        >
          {bio}
        </p>
      </div>

      {activeSocials.length > 0 && (
        <div className={cn('flex gap-2 p-4 md:p-7 bg-white', accentOnHover)}>
          {activeSocials.map(({ key, label, icon: Icon }) => (
            <a
              key={key}
              href={socials![key] as string}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${name} on ${label}`}
              className={cn(
                'inline-flex items-center justify-center size-10 shrink-0 rounded-full border border-black text-black bg-transparent',
                'hover:bg-black hover:text-white',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black',
                'transition-colors duration-200 ease-in-out',
              )}
            >
              <Icon className="size-[18px]" aria-hidden="true" />
            </a>
          ))}
        </div>
      )}
    </article>
  )
}
